import { isString } from '@dt/fe-utils/dist/lodash'
import MD5 from 'crypto-js/md5'
import { RouteComponentProps } from 'react-router-dom'
import * as H from 'history'
import { format, toDecimal, addParams } from '@dt/fe-utils'

let timer = null

// alipay miniprogram
let _isAliPayMiniProgram = false
window?.my?.getEnv(res => {
  _isAliPayMiniProgram = !!res?.miniprogram
})

/** 获取当前开发环境 */
export function getEnv () {
  const { env } = ENV
  return {
    isDebug: localStorage.getItem('isDebug') === '1' || process.env.NODE_ENV === 'test',
    isDev: process.env.NODE_ENV === 'development', // 本地开发环境
    isPrd: process.env.NODE_ENV === 'production', // 生产环境
    isTest: process.env.NODE_ENV === 'test',
    isBts: env === 'bts', // bts环境
  }
}

/** 获取当前系统信息 */
export function getPlatform () {
  const ua = navigator.userAgent.toLowerCase()
  const sysPlatform = navigator.platform.toLowerCase()
  const isIpad = ua.includes('ipad')
  const isAndroid = ua.includes('android')
  const isIOS = ua.includes('iphone os')
  const isIphoneX = isIOS && (screen.height >= 812 && screen.width >= 375)
  const isWeixin = ua.includes('micromessenger')
  const isWeixinMiniProgram = isWeixin && ua.includes('miniprogram') || window.__wxjs_environment === 'miniprogram'
  const isWeixinBrower = isWeixin && !isWeixinMiniProgram || window.__wxjs_environment === 'browser'
  let isWorkWeixin = isWeixin && ua.includes('wxwork')
  if (window?.boJudge?.checkGrayKey('workWeixinCanOpen')) {
    isWorkWeixin = false
  }
  const isComputerBrower = sysPlatform.includes('win') || sysPlatform.includes('mac')
  const isAlipay = ua.includes('alipay') || ua.includes('alipayclient')
  let isAliPayMiniProgram = (isAlipay || ua.includes('alipayide') || _isAliPayMiniProgram) && Config.inMiniApp
  const isDouyinMiniProgram = navigator.userAgent.includes('Douyin') || navigator.userAgent.includes('AwemeMicroApp') && Config.inMiniApp
  const isPhoneCase = !isDouyinMiniProgram && !isAliPayMiniProgram && !isWeixinMiniProgram && screen.width > 1024 && !window.cordova && Config.source === 'phoneCase'
  return {
    inMiniApp: Config.inMiniApp,
    isAndroid,
    isIOS,
    isPhone: isAndroid || isIOS,
    isIpad,
    isIphoneX,
    isComputerBrower, // 电脑浏览器
    isWeixin, // 微信环境
    isWeixinMiniProgram, // 小程序环境
    isWeixinBrower, // 微信浏览器环境（android下无法判断）
    isWorkWeixin, // 企业微信
    isAlipay, // 支付宝
    isAliPayMiniProgram, // 支付宝小程序
    isDouyinMiniProgram,
    isPhoneCase
  }
}

/**
 * history的全局拦截
 * @param {H.History} history - react-routet的history对象
 * @param {string} type - 跳转类型
 * @param {string} path - 跳转路由
 * @param {Record<string, any>} state - 路由数据
 * @param {[key: string]: string} addSearch - 零时拼接路由querystring
 */
export const historyOperation = function (history: H.History, type: 'push' | 'replace' = 'push', path: string | H.LocationDescriptor, state?: H.LocationState, addSearch?: {[key: string]: string}) {
  const { BRANDID, STOREID, TABLECODE, promotionId, otherType, codeType, isSupportSync, source, buyTogetherId, queueId } = Config
  if (!history || !path) return
  const params: Record<string, string | number> = {
    ct: codeType // 码类型
  }
  if (addSearch) {
    for (let key in addSearch) {
      params[key] = addSearch[key]
    }
  }
  BRANDID && (params.b = BRANDID)
  STOREID && (params.s = STOREID)
  TABLECODE && (params.t = TABLECODE)
  isSupportSync && (params.sync = 1)
  promotionId && (params.p = promotionId)
  source && (params.source = source)
  buyTogetherId && (params.bt = buyTogetherId)
  queueId && (params.queueId = queueId)
  otherType && (params.ot = otherType)
  if (typeof path === 'string') {
    let search = addParams({
      url: '?',
      params
    })
    history[type]?.({
      pathname: path,
      search,
      state
    })
  } else {
    const locationSearch = path?.search?.replace('?', '') || ''
    let search = addParams({
      url: `?${locationSearch}`,
      params
    })
    history[type]?.({
      ...path,
      search
    })
  }
}

/**
 * 获取子路由的路径
 * @param {RouteComponentProps['match']} match - react-router的match
 * @param {string} name - 子路由的名称
 */
export function getRoutePath (match: RouteComponentProps['match'], name: string) {
  return `${match.path}/${name}`
}

/**
 * 处理元素滚动
 * @param {boolean} toTop - 是否滚动到顶部
 * @param {number} scrollTop - 滚动距离
 * @param {number} duration - 滚动时间
 * @desc selector必须是IonContent组件
 */
export const handleEleScrollTo = ({ selector = 'ion-content.menu-container', toTop = false, x = 0, y = 0, duration = 300, timeout = 2000 }: { selector?: string; toTop?: boolean; x?: number; y?: number; duration?: number; timeout?: number } = {}) => {
  const eleContainer = document.querySelector(selector) as any
  return toTop
    ? eleContainer?.scrollToTop?.(duration)
    : eleContainer?.scrollToPoint?.(x, y, duration)
}

/**
 * 自定义元素滚动
 * @param {HTMLElement} event - 滚动父容器
 * @param {number} x - x轴滚动距离
 * @param {number} y - y轴滚动距离
 * @param {number} duration - 滚动时间
 */
export const scrollToPoint = async (event: HTMLElement, { x = 0, y = 0, duration = 100 }: { x?: number; y?: number; duration?: number }) => {
  const el = event
  if (duration < 32) {
    if (y != null) {
      el.scrollTop = y
    }
    if (x != null) {
      el.scrollLeft = x
    }
    return
  }
  let _resolve
  let startTime = 0
  const promise = new Promise(resolve => { _resolve = resolve })
  const fromY = el.scrollTop
  const fromX = el.scrollLeft
  const deltaY = y != null ? y - fromY : 0
  const deltaX = x != null ? x - fromX : 0
  // scroll loop
  const step = (timeStamp) => {
    const linearTime = Math.min(1, ((timeStamp - startTime) / duration)) - 1
    const easedT = Math.pow(linearTime, 3) + 1
    if (deltaY !== 0) {
      el.scrollTop = Math.floor((easedT * deltaY) + fromY)
    }
    if (deltaX !== 0) {
      el.scrollLeft = Math.floor((easedT * deltaX) + fromX)
    }
    if (easedT < 1) {
      requestAnimationFrame(step)
    } else {
      _resolve()
    }
  }
  // chill out for a frame first
  requestAnimationFrame(ts => {
    startTime = ts
    step(ts)
  })
  return promise
}

/** md5 */
// export function md5 (content: string) {
//   return _encrypt('md5', content)
// }
export function md5 (content: string) {
  return MD5(content).toString()
}

/** toast */
export function toast ({ message, time = 2000, position = 'top', cssClass = '' }: { message: string; time?: number; position?: 'top' | 'bottom' | 'middle'; cssClass?: string }) {
  const toast = document.createElement('ion-toast')
  toast.cssClass = 'mkt-toast ' + cssClass
  toast.position = position
  toast.message = message
  toast.duration = time
  document.body.appendChild(toast)
  return toast.present()
}

/** 检验密码 6-20 包含字母、数字 */
export function validPassword (password: string) {
  const reg = /^(?=.*[a-zA-Z])(?=.*\d)[^]{6,20}$/
  return reg.test(password)
}

/** format mkt error message 错误处理 */
export const fmtMktMessage = (codeMap: any, error: any) => {
  if (error && isString(error)) {
    let arr = `${error}`.split('-')
    let arrVar = arr.slice(1)
    let _code = codeMap[arr[0]]
    if (_code) {
      arrVar.forEach((item: string, index: number) => {
        _code = _code.replace(`\${${index}}`, item)
      })
    }
    toast({ message: _code })
  } else {
    if (error.message !== 'canceled') {
      toast({ message: error.message || `${i18n.t('mkt.common.error')}` })
    }
  }
  return false
}

/** 隐私 处理 */
export const formatShortPrivate = (str: string, end: number = 4, front: number = 0, encryptLen?: number) => {
  const priLen: number = encryptLen || str.length - end - front
  let resStr: string = str.substring(0, front)
  for (let i = 0; i < priLen; i++) {
    resStr += '*'
  }
  resStr += str.substr(str.length - end)
  return resStr
}

/** 折扣 处理 */
export const formatDiscount = (discount: number) => {
  if (i18n.language === 'zh-CN') {
    return i18n.t('mkt.common.discountXX', { cnt: toDecimal(discount * 10, 2) })
  }
  return i18n.t('mkt.common.discountXX', { cnt: toDecimal(100 - discount * 100, 0) })
}

/** 卡号 处理 */
export const formatCardNumber = (value: string) => {
  return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ')
}

export const showMsg = (msg: string, ShowTime: number = 1000) => {
  const DDMessage = document.getElementById('DDMessage')
  if (!DDMessage) {
    const DDMessageDom = document.createElement('div')
    const text = document.createTextNode(msg)
    DDMessageDom.appendChild(text)
    DDMessageDom.setAttribute('class', 'ddmessage-wrap')
    DDMessageDom.setAttribute('id', 'DDMessage')
    document.body.appendChild(DDMessageDom)

    const timeOut = setTimeout(() => {
      clearTimeout(timeOut)
      const DDMessage = document.getElementById('DDMessage')
      DDMessage && document.body.removeChild(DDMessage)
    }, ShowTime)
  }
}

// eslint-disable-next-line no-extend-native
String.prototype.zhLength = function () {
  let str = this + ''
  let len = str.length
  let reLen = 0
  for (let i = 0; i < len; i++) {
    if (str.charCodeAt(i) < 27 || str.charCodeAt(i) > 126) {
      reLen += 2
    } else {
      reLen++
    }
  }
  return reLen / 2
}

/**
 * 按照字节数切割
 * @param {string} str 作用字符串
 * @param {number} limit 限制多少字节长度
 * @param {string} pix 切割后坠
 * @returns {string}
 */
export const spliceZhLength = (str: string, limit: number, pix: string = '') => {
  let realLength = 0, len = str.length, charCode = -1
  for (let i = 0; i < len; i++) {
    charCode = str.charCodeAt(i)
    if (charCode >= 0 && charCode <= 128) {
      realLength += 1
    } else {
      realLength += 2
    }
    if (realLength > limit) {
      return str.substring(0, i) + pix
    }
  }
  return str
}

/**
 * @param flag : 'AM' | 'PM
 * 12小时转24小时
 * */
export const halfToAllDay = (hour: string, flag: string) => {
  let realHour = hour
  if (hour !== '12' && flag === 'PM') {
    realHour = Number(hour) + 12 + ''
  } else if (hour === '12' && flag === 'AM') {
    realHour = '00'
  }
  return realHour
}

export const setDocumentTitle = (title: string) => {
  const { isAlipay, isAliPayMiniProgram, inMiniApp } = getPlatform()
  if (isAlipay && !(isAliPayMiniProgram || inMiniApp) && window?.AlipayJSBridge) {
    console.log('AlipayJSBridge setTitle')
    window.AlipayJSBridge?.call('setTitle', {
      title,
    })
    return
  }
  document.title = title || document.title
  let iframe = document.getElementById('titleFrame')
  if (!iframe) {
    iframe = document.createElement('iframe')
    iframe.id = 'titleFrame'
    iframe.style.cssText = 'visibility: hidden;width: 0;height: 0'
  } else {
    timer && clearTimeout(timer)
  }
  if (iframe) {
    iframe.onload = function () {
      timer = setTimeout(() => {
        iframe?.remove()
      }, 200)
    }
    document.body.appendChild(iframe)
  }
}

export const randomNum = (minNum: number, maxNum: number) => {
  return parseInt(`${Math.random() * (maxNum - minNum + 1) + minNum}`, 10)
}

/**
 * 获取来源类型
 * codeType 桌码
 * storeOrderType 门店点餐方式
 */
export const getSourceType = (config: IConfig, storeOrderType: 0 | 1) => {
  const { codeType } = config
  const { isWeixin, isWeixinMiniProgram, isAliPayMiniProgram } = getPlatform()
  let type = (codeType === 'brand' || codeType === 'store') && storeOrderType ? 'onlineMobileSpecial' : codeType === 'brand' ? 'onlineMobileWebH5' : 'scanQRcode'
  if (isWeixinMiniProgram) {
    type = 'onlineWechatMiniProgram'
  } else if (isWeixin && type !== 'scanQRcode') {
    type = 'onlineWechatOfficialAccount'
  } else if (isAliPayMiniProgram) {
    type = 'onlineAlipayMiniProgram'
  }
  return type
}

// 检测element在可视区域
export const checkInVisible = (el: any) => {
  const pageHeight = document.documentElement.clientHeight
  const contentTop = el.getBoundingClientRect().top
  const contentHeight = el.offsetHeight
  return (contentTop < pageHeight && contentTop >= 0) || (contentTop < 0 && (contentTop + contentHeight > 0))
}

/**
 * 48小时转24
 */
export function time48To24 (timeString: string) {
  let [hour, minute] = timeString.split(':')
  let isNextDay = parseInt(hour) > 24 || (parseInt(hour) === 24 && parseInt(minute) > 0)
  return isNextDay ? `0${parseInt(hour) - 24}`.substr(-2) + `:${minute}` : timeString
}

export const formatTimeByToday = (time) => {
  if (!time) {
    return ''
  }

  if (format.toMonthDateYear(time) === format.toMonthDateYear(new Date().getTime())) {
    return format.toTime(time)
  }

  return format.toMonthDateTime(time)
}

export const amountFillZero = (amount: string) => {
  const spliArr: any[] = amount.split('.')
  let str: string = `${spliArr[0] || '0'}.${spliArr[1] || ''}`
  const len: number = spliArr[1] ? 2 - spliArr[1].length : 2
  for (let i = 0; i < len; i++) {
    str += '0'
  }
  return str
}

// 将金额格式化成整数
export const amountFormat = (amount: number | string) => Number(amountFillZero(`${amount}`).replace('.', ''))

// 两金额相加
export const amountAdd = (amountA: number | string, amountB: number | string) => {
  return (amountFormat(amountA) + amountFormat(amountB)) / 100
}

// 两金额相减
export const amountSubtract = (amountA: number | string, amountB: number | string) => {
  return (amountFormat(amountA) - amountFormat(amountB)) / 100
}

// diy适配
export const diyStyle = (data: IDiyModule['style'], index?: number) => {
  const windowWidth = window.innerWidth
  const per = windowWidth / 375
  const { width = 0, height = 0, top = 0, left = 0, } = data || {}
  const obj: any = {
    width: `${(typeof width === 'string' ? Number(width.replace('px', '')) : width) * per}px`,
    height: `${(typeof height === 'string' ? Number(height.replace('px', '')) : height) * per}px`,
  }
  if (data.top) {
    obj.top = `${(typeof top === 'string' ? Number(top.replace('px', '')) : top) * per}px`
  }
  if (data.left) {
    obj.left = `${(typeof left === 'string' ? Number(left.replace('px', '')) : left) * per}px`
  }
  if (index) {
    obj.zIndex = index + 1
  }
  return obj
}

/** 是否是gif */
export const isGif = (url) => {
  const regex = /\.gif$/i
  return regex.test(url)
}
